import React from "react";
import axios from "axios";

const POLL_INTERVAL_MS = 1000; // Poll every 1 second for faster cancel response

const STATUS_IDLE = 'idle';
const STATUS_PROCESSING = 'processing';
const STATUS_COMPLETE = 'complete';
const STATUS_FAILED = 'failed';
const STATUS_CANCELLED = 'cancelled';

export default class GeocodePropertiesTab extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            jobId: null,
            status: STATUS_IDLE,
            progress: 0,
            total: 0,
            done: 0,
            failed: 0,
            loading: false,
            canceling: false, // New state for canceling
            error: null,
            results: [],
            showFailedList: false,
        };

        this._pollTimer = null;
        this.startGeocoding = this.startGeocoding.bind(this);
        this.cancelJob = this.cancelJob.bind(this);
        this.resetJob = this.resetJob.bind(this);
        this.toggleFailedList = this.toggleFailedList.bind(this);
    }

    componentDidMount() {
        // Check for existing job in localStorage
        const savedJobId = localStorage.getItem('geocode_job_id');
        if (savedJobId) {
            this.checkJobStatus(savedJobId);
        }
    }

    componentWillUnmount() {
        if (this._pollTimer) {
            clearInterval(this._pollTimer);
            this._pollTimer = null;
        }
    }

    checkJobStatus(jobId) {
        const self = this;
        axios.get('/api/geocode-properties/status/' + jobId)
            .then(function (res) {
                if (res.data && res.data.success && res.data.job) {
                    const job = res.data.job;
                    self.setState({
                        jobId: job.jobId,
                        status: job.status,
                        progress: job.progress,
                        total: job.total,
                        done: job.done,
                        failed: job.failed,
                        results: job.results || [],
                    });

                    if (job.status === STATUS_PROCESSING) {
                        self.startPolling(job.jobId);
                    } else {
                        // Job finished, clear localStorage
                        localStorage.removeItem('geocode_job_id');
                    }
                } else {
                    // Job not found, clear localStorage
                    localStorage.removeItem('geocode_job_id');
                }
            })
            .catch(function () {
                // Job not found or error, clear localStorage
                localStorage.removeItem('geocode_job_id');
            });
    }

    startGeocoding() {
        const self = this;
        
        if (!confirm('This will find all properties without coordinates and start geocoding them. This may take several minutes. Continue?')) {
            return;
        }

        this.setState({ loading: true, error: null });

        axios.post('/api/geocode-properties/start')
            .then(function (res) {
                if (res.data && res.data.success) {
                    const jobId = res.data.jobId;
                    const total = res.data.total;

                    // Save jobId to localStorage
                    localStorage.setItem('geocode_job_id', jobId);

                    self.setState({
                        jobId: jobId,
                        status: STATUS_PROCESSING,
                        progress: 0,
                        total: total,
                        done: 0,
                        failed: 0,
                        loading: false,
                        error: null,
                        results: [],
                    });

                    self.startPolling(jobId);
                } else {
                    self.setState({
                        loading: false,
                        error: res.data.error || 'Failed to start geocoding job'
                    });
                }
            })
            .catch(function (err) {
                const errorMessage = self.parseErrorMessage(err);
                self.setState({
                    loading: false,
                    error: errorMessage
                });
            });
    }

    startPolling(jobId) {
        const self = this;
        
        if (this._pollTimer) {
            clearInterval(this._pollTimer);
        }

        this._pollTimer = setInterval(function () {
            axios.get('/api/geocode-properties/status/' + jobId)
                .then(function (res) {
                    if (res.data && res.data.success && res.data.job) {
                        const job = res.data.job;
                        
                        // Only clear canceling state if job actually stopped
                        const isFinished = job.status === STATUS_COMPLETE || 
                                         job.status === STATUS_FAILED || 
                                         job.status === STATUS_CANCELLED;
                        
                        self.setState({
                            status: job.status,
                            progress: job.progress,
                            done: job.done,
                            failed: job.failed,
                            results: job.results || [],
                            canceling: isFinished ? false : self.state.canceling, // Keep canceling state until job actually stops
                        });

                        if (isFinished) {
                            clearInterval(self._pollTimer);
                            self._pollTimer = null;
                            localStorage.removeItem('geocode_job_id');
                        }
                    }
                })
                .catch(function (err) {
                    console.error('[Geocode] Poll error:', err);
                });
        }, POLL_INTERVAL_MS);
    }

    cancelJob() {
        const self = this;
        
        if (!confirm('Are you sure you want to cancel the geocoding job?')) {
            return;
        }

        const jobId = this.state.jobId;
        
        // Set canceling state immediately
        this.setState({ canceling: true });
        
        axios.post('/api/geocode-properties/cancel/' + jobId)
            .then(function (res) {
                if (res.data && res.data.success) {
                    // Polling will detect the cancellation and update status
                    // Keep canceling state until job status changes to cancelled
                } else {
                    self.setState({ canceling: false });
                    alert('Failed to cancel job: ' + (res.data.error || 'Unknown error'));
                }
            })
            .catch(function (err) {
                self.setState({ canceling: false });
                alert('Error cancelling job: ' + self.parseErrorMessage(err));
            });
    }

    resetJob() {
        this.setState({
            jobId: null,
            status: STATUS_IDLE,
            progress: 0,
            total: 0,
            done: 0,
            failed: 0,
            loading: false,
            canceling: false,
            error: null,
            results: [],
            showFailedList: false,
        });
        localStorage.removeItem('geocode_job_id');
    }

    toggleFailedList() {
        this.setState({ showFailedList: !this.state.showFailedList });
    }

    parseErrorMessage(err) {
        if (err.response && err.response.data) {
            const errorData = err.response.data;
            if (typeof errorData === 'string') {
                return errorData;
            } else if (errorData.error) {
                return errorData.error;
            } else if (errorData.message) {
                return errorData.message;
            }
        } else if (err.message) {
            return err.message;
        } else if (err.code === 'ERR_NETWORK' || err.toString().indexOf('Network Error') !== -1) {
            return 'Cannot connect to server. Please ensure the API server is running.';
        }
        return 'Unknown error occurred';
    }

    renderProgressBar() {
        const progress = this.state.progress;
        const done = this.state.done;
        const failed = this.state.failed;
        const total = this.state.total;

        return (
            <div className='progress-section'>
                <div className='progress-label'>
                    Processing {done + failed} / {total} properties... {progress}%
                </div>
                <div className='progress-bar-track'>
                    <div className='progress-bar-fill' style={{width: progress + '%'}}/>
                </div>
            </div>
        );
    }

    renderResult() {
        const status = this.state.status;
        const done = this.state.done;
        const failed = this.state.failed;
        const total = this.state.total;
        const results = this.state.results;
        const showFailedList = this.state.showFailedList;

        const isCancelled = status === STATUS_CANCELLED;
        const isFailed = status === STATUS_FAILED;
        const isComplete = status === STATUS_COMPLETE;

        const failedProperties = results.filter(r => r.status === 'failed');

        return (
            <div className='result-section'>
                <div className={'result-header ' + (isFailed ? 'result-error' : isCancelled ? 'result-warning' : 'result-success')}>
                    {isFailed ? 'Geocoding Failed' : isCancelled ? 'Geocoding Cancelled' : 'Geocoding Complete'}
                </div>

                <div className='result-stats'>
                    <div className='stat'>
                        <span className='stat-number'>{total}</span>
                        <span className='stat-label'>Total Properties</span>
                    </div>
                    <div className='stat stat-success'>
                        <span className='stat-number'>{done}</span>
                        <span className='stat-label'>Geocoded Successfully</span>
                    </div>
                    {failed > 0 &&
                        <div className='stat stat-error'>
                            <span className='stat-number'>{failed}</span>
                            <span className='stat-label'>Failed</span>
                        </div>
                    }
                </div>

                {failedProperties.length > 0 &&
                    <div className='result-details'>
                        <div className='details-toggle details-error' onClick={this.toggleFailedList}>
                            Failed Properties ({failedProperties.length})
                        </div>
                        {showFailedList &&
                            <div className='details-list'>
                                {failedProperties.slice(0, 50).map(function (prop, i) {
                                    return (
                                        <div key={i} className='details-item details-error-item'>
                                            <strong>{prop.name || 'Unnamed'}</strong> — {prop.address}
                                            <br/>
                                            <span className='error-reason'>Reason: {prop.error || 'Unknown error'}</span>
                                        </div>
                                    );
                                })}
                                {failedProperties.length > 50 &&
                                    <div className='details-item'>...and {failedProperties.length - 50} more</div>
                                }
                            </div>
                        }
                    </div>
                }

                <button type='button' className='button' onClick={this.resetJob}>
                    Start Another Job
                </button>
            </div>
        );
    }

    render() {
        const status = this.state.status;
        const loading = this.state.loading;
        const canceling = this.state.canceling;
        const error = this.state.error;

        const isIdle = status === STATUS_IDLE;
        const isProcessing = status === STATUS_PROCESSING;
        const isFinished = status === STATUS_COMPLETE || status === STATUS_FAILED || status === STATUS_CANCELLED;

        return (
            <div>
                <div className='upload-title-row'>
                    <div className='upload-file-title'>Geocode Properties</div>
                </div>

                <div className='upload-instructions'>
                    <p>
                        This tool will find all properties in the database that are missing latitude/longitude coordinates 
                        and automatically geocode them using Google Maps.
                    </p>
                    <p>
                        <strong>Note:</strong> Only properties with valid Address, City, and State will be processed. 
                        The job may take several minutes depending on the number of properties.
                    </p>
                </div>

                {error &&
                    <div className='upload-file-text' style={{color: '#FF5050', marginTop: '20px'}}>
                        {error}
                    </div>
                }

                {isIdle && !loading &&
                    <div style={{marginTop: '20px'}}>
                        <button 
                            type='button' 
                            className='button submit' 
                            onClick={this.startGeocoding}
                        >
                            Start Geocoding
                        </button>
                    </div>
                }

                {loading &&
                    <div style={{marginTop: '20px'}}>
                        <div className='progress-label'>Starting geocoding job...</div>
                    </div>
                }

                {isProcessing &&
                    <div>
                        {this.renderProgressBar()}
                        <div style={{marginTop: '15px'}}>
                            {canceling ? (
                                <button 
                                    type='button' 
                                    className='button' 
                                    disabled
                                    style={{backgroundColor: '#999', cursor: 'not-allowed'}}
                                >
                                    Canceling...
                                </button>
                            ) : (
                                <button 
                                    type='button' 
                                    className='button' 
                                    onClick={this.cancelJob}
                                    style={{backgroundColor: '#d9534f'}}
                                >
                                    Cancel Job
                                </button>
                            )}
                        </div>
                    </div>
                }

                {isFinished && this.renderResult()}
            </div>
        );
    }
}
